import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useCallback, useState } from "react";

// Lazy load Puck (~300KB) - only loaded when CMS page data exists
const PuckRenderer = lazy(async () => {
	const [{ Render }] = await Promise.all([
		import("@measured/puck"),
		import("@measured/puck/puck.css"),
	]);
	return { default: Render };
});

const ProjectDetailModal = lazy(() =>
	import("@/components/projects/project-detail-modal").then((m) => ({
		default: m.ProjectDetailModal,
	})),
);

import { AboutLanding } from "@/components/home/about-landing";
import { CollaborationsCarousel } from "@/components/home/collaborations-carousel";
import { FeaturedProjects } from "@/components/home/featured-projects";
import { HeroSection } from "@/components/home/hero-section";
import { HomeContactSection } from "@/components/home/home-contact-section";
import { ServicesSection } from "@/components/services/services-section";
import { type PuckPageData, puckConfig } from "@/lib/puck/config";
import {
	allSiteSettingsQueryOptions,
	featuredProjectsQueryOptions,
	featuredTestimonialsQueryOptions,
	pageBySlugQueryOptions,
	publicStatsQueryOptions,
	servicesQueryOptions,
	siteSettingsQueryOptions,
} from "@/lib/query-options";

export const Route = createFileRoute("/")({
	loader: async ({ context }) => {
		/**
		 * SSR: Resilient prefetching with Promise.allSettled
		 * - Uses per-request queryClient from router context (prevents cache bleeding)
		 * - Prefetches all homepage data in parallel
		 * - Individual failures don't crash the entire page
		 * - Components fall back to static data if CMS unavailable
		 */
		const { queryClient } = context;

		await Promise.allSettled([
			// Puck-managed page content
			queryClient.prefetchQuery(pageBySlugQueryOptions("home")),
			// Featured projects for hero/grid
			queryClient.prefetchQuery(featuredProjectsQueryOptions()),
			// Public stats (project count, years experience)
			queryClient.prefetchQuery(publicStatsQueryOptions()),
			// Testimonials for carousel
			queryClient.prefetchQuery(featuredTestimonialsQueryOptions()),
			// Services for services section
			queryClient.prefetchQuery(servicesQueryOptions({ featured: true })),
			// All site settings (includes homepage, contact, about, socialLinks)
			queryClient.prefetchQuery(allSiteSettingsQueryOptions()),
			// Contact settings for HomeContactSection (keep individual for fallback)
			queryClient.ensureQueryData(siteSettingsQueryOptions("contact")),
			// About settings for AboutLanding
			queryClient.ensureQueryData(siteSettingsQueryOptions("about")),
		]);
	},
	component: HomeComponent,
});

function HomeComponent() {
	// Use cached data from SSR prefetch - instant hydration, no loading spinner
	const { data: homePage, isLoading } = useQuery({
		...pageBySlugQueryOptions("home"),
		// Override staleTime for homepage - frequently visited
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

	// Show loading spinner only if cache miss (rare edge case)
	if (isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-background">
				<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
			</div>
		);
	}

	// If we have a published Puck page, render it with lazy-loaded Puck
	if (homePage?.puck_data) {
		return (
			<div className="min-h-screen">
				<Suspense
					fallback={
						<div className="flex min-h-screen items-center justify-center bg-background">
							<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
						</div>
					}
				>
					<PuckRenderer
						config={puckConfig}
						data={homePage.puck_data as PuckPageData}
					/>
				</Suspense>
			</div>
		);
	}

	// Otherwise, render the static homepage (fallback)
	return <StaticHomeComponent />;
}

// Type for homepage settings from CMS
type HomepageSettings = {
	hero?: {
		headline?: string;
		subtitle?: string;
		bio?: string;
		photo?: { url?: string } | null;
	};
	stats?: Array<{ value: string; label: string }>;
	cta?: {
		text?: string;
		link?: string;
		secondaryText?: string;
		secondaryLink?: string;
	};
	showFeaturedProjects?: boolean;
	showTestimonials?: boolean;
	showCollaborations?: boolean;
};

/**
 * Static Homepage Component (Fallback)
 *
 * Used when no published Puck page exists for "home" slug.
 * This ensures the site always has a working homepage.
 */
function StaticHomeComponent() {
	// Modal state management for project detail modal
	const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
		null,
	);
	const [isModalOpen, setIsModalOpen] = useState(false);

	// Fetch all site settings (includes homepage)
	const { data: siteSettings } = useQuery(allSiteSettingsQueryOptions());

	// Extract homepage settings with fallback
	const homepage = (siteSettings?.homepage as HomepageSettings) ?? {};

	// Handle project card click - open modal with selected project
	const handleProjectClick = useCallback((projectId: string) => {
		setSelectedProjectId(projectId);
		setIsModalOpen(true);
	}, []);

	// Handle modal close - reset state
	const handleModalClose = useCallback(() => {
		setIsModalOpen(false);
		// Delay clearing projectId to allow close animation to complete
		setTimeout(() => setSelectedProjectId(null), 200);
	}, []);

	return (
		<div className="min-h-screen overflow-x-hidden">
			{/* Hero Section - with carousel integrated */}
			<section className="relative overflow-hidden bg-background">
				{/* Sophisticated gradient overlays */}
				<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(198_32%_35%_/_0.05),transparent)] dark:bg-[radial-gradient(ellipse_at_top,hsl(198_45%_60%_/_0.02),transparent)]" />

				{/* Subtle grid pattern with better contrast */}
				<div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,hsl(198_15%_85%_/_0.3)_1px,transparent_1px),linear-gradient(to_bottom,hsl(198_15%_85%_/_0.3)_1px,transparent_1px)] bg-[size:2rem_2rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] dark:bg-[linear-gradient(to_right,hsl(198_15%_25%_/_0.3)_1px,transparent_1px),linear-gradient(to_bottom,hsl(198_15%_25%_/_0.3)_1px,transparent_1px)]" />

				<div className="relative w-full">
					<HeroSection
						headline={homepage.hero?.headline}
						subtitle={homepage.hero?.subtitle}
						bio={homepage.hero?.bio}
						photoSrc={homepage.hero?.photo?.url}
						stats={homepage.stats}
						ctaText={homepage.cta?.text}
						ctaLink={homepage.cta?.link}
						secondaryCtaText={homepage.cta?.secondaryText}
						secondaryCtaLink={homepage.cta?.secondaryLink}
					/>
				</div>

				{/* Collaborations Carousel - integrated into hero */}
				<CollaborationsCarousel className="bg-transparent" />
			</section>

			{/* Featured Projects Section - alternating plain */}
			<section className="bg-background">
				<FeaturedProjects onProjectClick={handleProjectClick} />
			</section>

			{/* Services Section - alternating stripe */}
			<section className="bg-muted/10 dark:bg-muted/5">
				<ServicesSection />
			</section>

			{/* About Landing Section - alternating plain */}
			<section className="bg-background">
				<AboutLanding />
			</section>

			{/* Contact Section - alternating stripe */}
			<section className="bg-muted/10 dark:bg-muted/5">
				<HomeContactSection />
			</section>

			{/* Project Detail Modal - lazy loaded */}
			<Suspense fallback={null}>
				<ProjectDetailModal
					isOpen={isModalOpen}
					onClose={handleModalClose}
					projectId={selectedProjectId}
				/>
			</Suspense>
		</div>
	);
}
